"use client";

import { useEffect } from "react";
import type { HomeCopy, LocaleKey } from "./copy";
import type { RemontCopy } from "./remont-pod-klucz-copy";
import ClientBits from "./ClientBits";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";
import FloatingCTA from "./FloatingCTA";
import HeroQuizButton from "./HeroQuizButton";
import { useAnalytics } from "@/hooks/useAnalytics";

interface Props {
  locale: LocaleKey;
  homeCopy: HomeCopy;
  copy: RemontCopy;
}

export default function RemontPodKluczPage({ locale, homeCopy, copy }: Props) {
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    trackEvent("page_loaded", "landing_page", "remont-pod-klucz", {
      icp: "post-purchase",
      language: locale,
    });
  }, [trackEvent, locale]);

  return (
    <div className="hestia-page" style={{ fontFamily: "var(--font-inter), Inter, sans-serif" }}>
      <div className="main-wrapper">
        <div className="main-overlay" />
        <SiteHeader copy={homeCopy} locale={locale} variant="default" />

        {/* === Block 1 — Hero ====================================== */}
        <section className="hero-section container">
          <div className="hero-grid">
            <div className="hero-info">
              <h1 className="hero-title">
                {copy.hero.titleSegments.map((seg, i) =>
                  seg.emphasis ? (
                    <strong key={i} className="accent">{seg.text}</strong>
                  ) : (
                    <span key={i}>{seg.text}</span>
                  )
                )}
              </h1>
              <p className="hero-text">{copy.hero.text}</p>
              <div className="btn-container" style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 420 }}>
                <button
                  type="button"
                  className="oferta-cta-wa open-callback"
                  data-cta-source="remont-hero-primary"
                >
                  <div className="oferta-cta-wa__inner">
                    <div className="oferta-cta-wa__text">
                      <div className="oferta-cta-wa__title">
                        {locale === "ru" ? "Получить диагностику рисков" : "Otrzymaj diagnozę ryzyka"}
                      </div>
                      <div className="oferta-cta-wa__sub">
                        {locale === "ru" ? "Экономия до 80 000 zł до подписания договора" : "Oszczędność do 80 000 zł przed podpisem umowy"}
                      </div>
                    </div>
                    <div className="oferta-cta-wa__icon-box" aria-hidden>🛡</div>
                  </div>
                </button>
                <HeroQuizButton
                  locale={locale}
                  page="/uslugi/remont-pod-klucz"
                  icp="general"
                  title={locale === "ru" ? "Пример аудита" : "Przykład audytu"}
                  sub={locale === "ru" ? "12 скрытых пунктов договора, на которых покупатели теряют деньги" : "12 ukrytych punktów umowy, na których kupujący tracą pieniądze"}
                  ctaSource="remont-hero-quiz"
                />
              </div>
              <div className="cta-microproof">
                <span className="cta-microproof__strong">★ 5,0 Google</span>
                <span className="cta-microproof__sep">·</span>
                {locale === "ru" ? "22 застройщика в мониторинге" : "22 deweloperów w monitoringu"}
                <span className="cta-microproof__sep">·</span>
                <span className="cta-microproof__strong">{locale === "ru" ? "средняя экономия 38 000 zł" : "średnie oszczędności 38 000 zł"}</span>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* === Block 2 — Pain ======================================== */}
      <section className="kredyt-section kredyt-section--cream">
        <div className="container">
          <div className="kredyt-section-header">
            <h2 className="kredyt-h2">{copy.pain.h2}</h2>
          </div>
          <div className="pain-grid">
            {copy.pain.cards.map((card, i) => (
              <div key={i} className="pain-card">
                <h3 className="pain-title">{card.title}</h3>
                <p className="pain-context">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === Block 3 — New Way ===================================== */}
      <section className="kredyt-section">
        <div className="container">
          <div className="kredyt-section-header">
            <h2 className="kredyt-h2">{copy.newWay.h2}</h2>
          </div>
          <div className="newway-body" style={{ maxWidth: 820, margin: "0 auto" }}>
            {copy.newWay.body.map((p, i) => (
              <p key={i} style={{ fontSize: 17, lineHeight: 1.6, marginBottom: 18 }}>
                {p}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* === Block 4 — Value Stack ================================= */}
      <section className="kredyt-section kredyt-section--cream">
        <div className="container">
          <div className="kredyt-section-header">
            <h2 className="kredyt-h2">{copy.valueStack.h2}</h2>
          </div>
          <div className="value-grid" style={{ display: "grid", gap: 20 }}>
            {copy.valueStack.modules.map((m, i) => (
              <div
                key={i}
                className="value-module"
                style={{
                  padding: "20px 24px",
                  background: "#fff",
                  borderRadius: 16,
                  borderLeft: "4px solid #C9A36A",
                }}
              >
                <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: "#0F2542" }}>
                  {i + 1}. {m.title}
                </h3>
                <p style={{ fontSize: 16, lineHeight: 1.55, color: "#2a3142" }}>{m.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === Block 5 — Pricing ===================================== */}
      <section className="kredyt-section">
        <div className="container">
          <div className="kredyt-section-header">
            <h2 className="kredyt-h2">{copy.pricing.h2}</h2>
            <p className="kredyt-subhead">{copy.pricing.narrativeBody}</p>
          </div>
          <div
            className="pricing-tiers"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 20,
              marginTop: 32,
            }}
          >
            {copy.pricing.tiers.map((tier, i) => (
              <div
                key={i}
                style={{
                  padding: "28px 24px",
                  background: i === 1 ? "#0F2542" : "#fff",
                  color: i === 1 ? "#fff" : "#0F2542",
                  borderRadius: 16,
                  border: "1px solid rgba(15,37,66,0.12)",
                  boxShadow: i === 1 ? "0 14px 32px rgba(15,37,66,0.18)" : "0 4px 12px rgba(0,0,0,0.04)",
                }}
              >
                <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>{tier.name}</h3>
                <p style={{ fontSize: 26, fontWeight: 800, marginBottom: 12, color: i === 1 ? "#C9A36A" : "#577AE0" }}>
                  {tier.range}
                </p>
                <p style={{ fontSize: 15, lineHeight: 1.5, opacity: i === 1 ? 0.92 : 0.85 }}>{tier.desc}</p>
              </div>
            ))}
          </div>
          <p style={{ marginTop: 28, textAlign: "center", fontSize: 16, color: "#577AE0", fontWeight: 600 }}>
            {copy.pricing.closing}
          </p>
        </div>
      </section>

      {/* === Block 6 — Authority + Case ============================ */}
      <section className="kredyt-section kredyt-section--cream">
        <div className="container">
          <div className="kredyt-section-header">
            <h2 className="kredyt-h2">{copy.authority.h2}</h2>
            <p className="kredyt-subhead">{copy.authority.intro}</p>
          </div>
          <div
            style={{
              maxWidth: 760,
              margin: "0 auto",
              padding: "28px 32px",
              background: "#fff",
              borderRadius: 16,
              borderLeft: "4px solid #C9A36A",
            }}
          >
            <h3 style={{ fontSize: 19, fontWeight: 700, color: "#0F2542", marginBottom: 12 }}>
              {copy.authority.caseTitle}
            </h3>
            <p style={{ fontSize: 16, lineHeight: 1.55, color: "#2a3142", marginBottom: 16 }}>
              {copy.authority.caseBody}
            </p>
            <p
              style={{
                fontSize: 16,
                lineHeight: 1.55,
                color: "#0F2542",
                fontWeight: 600,
                padding: "16px 18px",
                background: "rgba(201,163,106,0.12)",
                borderRadius: 8,
              }}
            >
              {copy.authority.caseResult}
            </p>
          </div>
        </div>
      </section>

      {/* === Block 7 — Takeaway / Hard Filter ====================== */}
      <section className="kredyt-section">
        <div className="container">
          <div className="kredyt-section-header">
            <h2 className="kredyt-h2">{copy.takeaway.h2}</h2>
            <p className="kredyt-subhead">{copy.takeaway.body}</p>
          </div>
          <ul style={{ maxWidth: 760, margin: "0 auto", listStyle: "none", padding: 0 }}>
            {copy.takeaway.disqualifiers.map((d, i) => (
              <li
                key={i}
                style={{
                  display: "flex",
                  gap: 14,
                  marginBottom: 16,
                  padding: "16px 18px",
                  background: "#fff5f5",
                  borderRadius: 12,
                  borderLeft: "4px solid #C95252",
                }}
              >
                <span style={{ color: "#C95252", fontWeight: 800, fontSize: 18, flexShrink: 0 }}>✕</span>
                <span style={{ fontSize: 16, lineHeight: 1.55, color: "#2a3142" }}>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* === Block 8 — FAQ accordion =============================== */}
      <section className="kredyt-section kredyt-section--cream oferta-faq-section">
        <div className="container">
          <div className="kredyt-section-header">
            <h2 className="kredyt-h2">{copy.faq.h2}</h2>
          </div>
          <div className="oferta-faq-list">
            {copy.faq.items.map((item, i) => (
              <details key={i} className="oferta-faq-item">
                <summary className="oferta-faq-q">
                  <span>{item.q}</span>
                </summary>
                <div className="oferta-faq-a">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* === Block 9 — Final CTA ================================== */}
      <section className="kredyt-section" style={{ background: "#0F2542", color: "#fff" }}>
        <div className="container" style={{ textAlign: "center", padding: "48px 16px" }}>
          <h2 style={{ fontSize: 28, fontWeight: 800, marginBottom: 16 }}>
            {locale === "ru"
              ? "Готовы превратить квартиру в готовый дом?"
              : "Gotowy zamienić mieszkanie w gotowy dom?"}
          </h2>
          <p style={{ fontSize: 17, lineHeight: 1.5, marginBottom: 28, opacity: 0.88, maxWidth: 580, margin: "0 auto 28px" }}>
            {locale === "ru"
              ? "Бесплатная консультация 60 минут. Покажем 3 варианта планировки и дадим конкретную смету."
              : "Bezpłatna konsultacja 60 minut. Pokażemy 3 warianty układu i damy konkretny kosztorys."}
          </p>
          <button
            type="button"
            className="oferta-hero-primary-cta open-callback"
            data-cta-source="remont-final-cta"
            style={{ margin: "0 auto" }}
          >
            <span className="oferta-hero-primary-cta-title">{copy.hero.ctaPrimary}</span>
            <span className="oferta-hero-primary-cta-sub">{copy.hero.ctaSub}</span>
          </button>
        </div>
      </section>

      <SiteFooter copy={homeCopy} />
      <ClientBits copy={homeCopy} locale={locale} />
      <FloatingCTA icp="home" label={locale === "ru" ? "Получить диагностику рисков" : "Otrzymaj diagnozę ryzyka"} />
    </div>
  );
}
